import { type PropsWithChildren, useMemo } from 'react';
import { StyleSheetManager } from 'styled-components';
import { i18nInit } from '@elfsight/lib-i18n';
import { useCustomJS, useDefineAPISchema } from '@elfsight/widget-js-api';
import {
  CustomCSS,
  createCustomCSSPrefixClassName,
  EmbedRoot,
  useFontsLoader
} from '@elfsight/widget-ui';
import { StorageProvider } from '@elfsight/widget-utils';
import { I18nProvider } from '@lingui/react';
import { useSettings } from '@wu/widget-core';
import { meta } from './meta';
import type { BannerSettings } from './types';
import { Widget } from './widget/widget';

export function Banner(_settings: BannerSettings) {
  return (
    <BannerProviders>
      <Widget />
    </BannerProviders>
  );
}

function BannerProviders({ children }: PropsWithChildren) {
  const {
    customCSS,
    customJS,
    editMode,
    font,
    renderThumbnailMode,
    width,
    widgetId
  } = useSettings();

  const customCSSPrefixClassName = createCustomCSSPrefixClassName(
    'banner',
    widgetId
  );

  useDefineAPISchema();

  useCustomJS(customJS, editMode || renderThumbnailMode);
  useFontsLoader([font]);

  const i18n = useMemo(() => i18nInit(), []);

  return (
    <I18nProvider i18n={i18n}>
      <StorageProvider widgetId={widgetId!} name={meta.name}>
        <StyleSheetManager
          shouldForwardProp={(propName) => !propName.startsWith('_')}
        >
          <CustomCSS
            customCSS={customCSS}
            prefixClassName={customCSSPrefixClassName}
          />
          <EmbedRoot
            customCSSClassName={customCSSPrefixClassName}
            width={width}
          >
            {children}
          </EmbedRoot>
        </StyleSheetManager>
      </StorageProvider>
    </I18nProvider>
  );
}
